import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { catchyTextFieldSx } from '../../theme/formStyles';
import { getGoogleMapsApiKey } from '../gps/mapUtils';
import { loadGoogleMaps } from '../gps/googleMapsLoader';

const INDIA_LOCATIONS = [
  'Mumbai, Maharashtra',
  'Pune, Maharashtra',
  'Nagpur, Maharashtra',
  'Nashik, Maharashtra',
  'Aurangabad, Maharashtra',
  'New Delhi, Delhi',
  'Noida, Uttar Pradesh',
  'Ghaziabad, Uttar Pradesh',
  'Gurugram, Haryana',
  'Faridabad, Haryana',
  'Jaipur, Rajasthan',
  'Jodhpur, Rajasthan',
  'Ahmedabad, Gujarat',
  'Surat, Gujarat',
  'Vadodara, Gujarat',
  'Rajkot, Gujarat',
  'Indore, Madhya Pradesh',
  'Bhopal, Madhya Pradesh',
  'Lucknow, Uttar Pradesh',
  'Kanpur, Uttar Pradesh',
  'Varanasi, Uttar Pradesh',
  'Patna, Bihar',
  'Kolkata, West Bengal',
  'Bhubaneswar, Odisha',
  'Hyderabad, Telangana',
  'Warangal, Telangana',
  'Bengaluru, Karnataka',
  'Mysuru, Karnataka',
  'Hubballi, Karnataka',
  'Chennai, Tamil Nadu',
  'Coimbatore, Tamil Nadu',
  'Madurai, Tamil Nadu',
  'Kochi, Kerala',
  'Thiruvananthapuram, Kerala',
  'Visakhapatnam, Andhra Pradesh',
  'Vijayawada, Andhra Pradesh',
  'Raipur, Chhattisgarh',
  'Ranchi, Jharkhand',
  'Guwahati, Assam',
  'Chandigarh',
  'Ludhiana, Punjab',
  'Amritsar, Punjab',
  'Dehradun, Uttarakhand',
  'Jammu, Jammu and Kashmir',
  'Panaji, Goa',
  'Bhiwandi, Maharashtra',
  'Vapi, Gujarat',
  'Mundra, Gujarat',
  'JNPT / Nhava Sheva, Maharashtra',
  'ICD Tughlakabad, Delhi',
];

interface IndiaLocationSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
}

export const IndiaLocationSelect: React.FC<IndiaLocationSelectProps> = ({
  label,
  value,
  onChange,
  required,
  placeholder = 'Search Indian city or location...',
  size = 'small',
}) => {
  const [input, setInput] = useState(value || '');
  const [places, setPlaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(value || '');
  }, [value]);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setPlaces([]);
      return undefined;
    }
    const key = getGoogleMapsApiKey();
    if (!key) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      loadGoogleMaps(key)
        .then(() => {
          const service = new google.maps.places.AutocompleteService();
          service.getPlacePredictions(
            {
              input: q,
              componentRestrictions: { country: 'in' },
            },
            (predictions, status) => {
              if (cancelled) return;
              setLoading(false);
              if (status !== 'OK' || !predictions) {
                setPlaces([]);
                return;
              }
              setPlaces(predictions.map((p) => p.description));
            },
          );
        })
        .catch(() => {
          if (!cancelled) {
            setLoading(false);
            setPlaces([]);
          }
        });
    }, 280);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [input]);

  const options = useMemo(() => {
    const q = input.trim().toLowerCase();
    const local = q
      ? INDIA_LOCATIONS.filter((loc) => loc.toLowerCase().includes(q))
      : INDIA_LOCATIONS.slice(0, 12);
    const merged = [...places, ...local.filter((loc) => !places.includes(loc))];
    if (value && !merged.includes(value)) merged.unshift(value);
    return merged.slice(0, 20);
  }, [input, places, value]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value || null}
      inputValue={input}
      onInputChange={(_e, next, reason) => {
        setInput(next);
        if (reason === 'input') onChange(next);
      }}
      onChange={(_e, next) => {
        const loc = typeof next === 'string' ? next : next || '';
        onChange(loc);
        setInput(loc);
      }}
      filterOptions={(opts) => opts}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          size={size}
          sx={catchyTextFieldSx}
          helperText={required ? 'Search and select an India location' : undefined}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
